import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Paper, Button, RadioGroup, FormControlLabel, Radio,
  TextField, Alert, CircularProgress, Link, Card, CardContent,
} from '@mui/material';
import { useMedicationCart } from '../../contexts/MedicationCartContext';
import { createPublicOrder, FulfillmentType, listPickupBranches, PickupBranch } from '../../api/publicOrders';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorRetry } from '../../components/ui/ErrorRetry';


export const PublicCheckout: React.FC = () => {
  const navigate = useNavigate();
  const { items, clearCart } = useMedicationCart();
  const [fulfillmentType, setFulfillmentType] = useState<FulfillmentType>('PICKUP');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [pickupBranchId, setPickupBranchId] = useState<string | null>(null);

  const branchesQuery = useQuery({
    queryKey: ['pickup-branches'],
    queryFn: listPickupBranches,
    enabled: fulfillmentType === 'PICKUP',
  });

  const mutation = useMutation({
    mutationFn: () => createPublicOrder({
      fulfillmentType,
      deliveryAddress: fulfillmentType === 'DELIVERY' ? deliveryAddress : undefined,
      notes: notes || undefined,
      items: items.map((i) => ({
        pharmacyProductId: i.pharmacyProductId,
        quantity: i.quantity,
      })),
      pickupBranchId: fulfillmentType === 'PICKUP' ? pickupBranchId ?? undefined : undefined,
    }),
    onSuccess: (order) => {
      clearCart();
      navigate(`/mis-pedidos/${order.trackingUUID}`);
    },
  });

  if (items.length === 0) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 6, px: 3 }}>
        <EmptyState
          title="Tu carrito está vacío"
          cta={{ label: 'Buscar medicamentos', to: '/buscar' }}
        />
      </Box>
    );
  }

  const subtotal = items.reduce((sum, i) => sum + Number(i.price) * i.quantity, 0);

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', mt: { xs: 2, sm: 4 }, px: 3, pb: 6 }}>
      <PageHeader title="Confirmar pedido" />

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'text.headline' }}>Productos</Typography>
          <Link component={RouterLink} to="/buscar" sx={{ color: 'primary.main', fontSize: 14 }}>
            Modificar carrito
          </Link>
        </Box>

        {items.map((item) => (
          <Box key={item.pharmacyProductId} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 500 }}>{item.name}</Typography>
              <Typography variant="caption" color="text.secondary">{item.chainName}</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mx: 2 }}>
              × {item.quantity}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', minWidth: 90, textAlign: 'right' }}>
              RD$ {(Number(item.price) * item.quantity).toFixed(2)}
            </Typography>
          </Box>
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body1" sx={{ color: 'text.headline', fontWeight: 500 }}>Subtotal</Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
            RD$ {subtotal.toFixed(2)}
          </Typography>
        </Box>
      </CardContent></Card>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ color: 'text.headline', mb: 2 }}>Entrega</Typography>
        <RadioGroup value={fulfillmentType} onChange={(e) => setFulfillmentType(e.target.value as FulfillmentType)}>
          <FormControlLabel value="DELIVERY" control={<Radio />} label="Delivery a domicilio" />
          <FormControlLabel value="PICKUP" control={<Radio />} label="Recoger en PharmaCana" />
        </RadioGroup>
        {fulfillmentType === 'DELIVERY' && (
          <TextField
            fullWidth multiline minRows={2}
            label="Dirección de entrega"
            value={deliveryAddress}
            onChange={(e) => setDeliveryAddress(e.target.value)}
            sx={{ mt: 2 }} required
          />
        )}
        {fulfillmentType === 'PICKUP' && (
          <Box sx={{ mt: 2 }}>
            {branchesQuery.isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} sx={{ color: 'text.headline' }} />
              </Box>
            )}

            {branchesQuery.isError && (
              <Box sx={{ mb: 1 }}>
                <ErrorRetry
                  message="Error al cargar sucursales."
                  onRetry={() => branchesQuery.refetch()}
                />
              </Box>
            )}

            {branchesQuery.data && branchesQuery.data.length === 0 && (
              <Alert severity="info">
                Por el momento no hay sucursales disponibles para retiro. Por favor selecciona delivery.
              </Alert>
            )}

            {branchesQuery.data && branchesQuery.data.length > 0 && (
              <>
                <Typography variant="body2" sx={{ color: 'text.headline', mb: 1, fontWeight: 500 }}>Sucursal:</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {branchesQuery.data.map((branch: PickupBranch) => {
                    const selected = pickupBranchId === branch.id;
                    return (
                      <Paper
                        key={branch.id}
                        elevation={0}
                        onClick={() => setPickupBranchId(branch.id)}
                        sx={{
                          cursor: 'pointer',
                          p: 1.5,
                          border: '2px solid',
                          borderColor: selected ? 'primary.main' : 'divider',
                          borderRadius: 2,
                          bgcolor: selected ? 'primary.softBg' : 'background.paper',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <Radio checked={selected} value={branch.id} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600, color: 'text.headline' }}>{branch.name}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{branch.address}</Typography>
                          {branch.phoneNumber && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              📞 {branch.phoneNumber}
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        )}
      </CardContent></Card>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ color: 'text.headline', mb: 2 }}>Notas (opcional)</Typography>
        <TextField
          fullWidth multiline minRows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Información adicional para la farmacia"
        />
      </CardContent></Card>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(mutation.error as any)?.response?.data?.message ?? 'Error al confirmar el pedido.'}
        </Alert>
      )}

      <Button
        fullWidth variant="contained" size="large"
        disabled={
          mutation.isPending ||
          (fulfillmentType === 'DELIVERY' && !deliveryAddress.trim()) ||
          (fulfillmentType === 'PICKUP'   && !pickupBranchId)
        }
        color="primary"
        onClick={() => mutation.mutate()}
        sx={{ py: 1.5 }}>
        {mutation.isPending ? <CircularProgress size={22} color="inherit" /> : 'Confirmar pedido'}
      </Button>
    </Box>
  );
};
